import React, { useMemo } from 'react';
import { useSequence } from '../../context/SequenceContext';
import SafeIcon from '../../common/SafeIcon';

const MetricCard = ({ label, value, detail, icon, tone }) => (
  <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{label}</p>
        <p className="text-3xl font-black text-slate-900 mt-2">{value}</p>
        <p className="text-xs text-slate-500 font-medium mt-1">{detail}</p>
      </div>
      <div className={`p-3 rounded-xl ${tone}`}>
        <SafeIcon icon={icon} className="w-5 h-5" />
      </div>
    </div>
  </div>
);

const SequencePerformanceAnalytics = () => {
  const { sequences, runs, tasks } = useSequence();

  const analytics = useMemo(() => {
    const enrolled = runs.length;
    const active = runs.filter(run => run.status === 'Active').length;
    const completed = runs.filter(run => run.status === 'Completed').length;
    const pending = tasks.filter(task => task.status === 'Pending').length;
    const completionRate = enrolled ? Math.round((completed / enrolled) * 100) : 0;

    const sequenceRows = sequences.map(sequence => {
      const sequenceRuns = runs.filter(run => run.sequence_id === sequence.sequence_id);
      const sequenceTasks = tasks.filter(task => task.sequence_name === sequence.name);
      const sequenceCompleted = sequenceRuns.filter(run => run.status === 'Completed').length;
      const rate = sequenceRuns.length
        ? Math.round((sequenceCompleted / sequenceRuns.length) * 100)
        : 0;

      return {
        ...sequence,
        enrolled: sequenceRuns.length,
        active: sequenceRuns.filter(run => run.status === 'Active').length,
        pending: sequenceTasks.filter(task => task.status === 'Pending').length,
        completionRate: rate
      };
    });

    return { enrolled, active, completed, pending, completionRate, sequenceRows };
  }, [runs, sequences, tasks]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-black text-slate-900">Sekvensytelse</h2>
        <p className="text-sm text-slate-500 font-medium mt-1">
          Følg påmeldinger, aktivitet og gjennomføring på tvers av sekvensene.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <MetricCard label="Totalt påmeldt" value={analytics.enrolled} detail="Alle sekvenser" icon="👥" tone="bg-blue-50 text-blue-600" />
        <MetricCard label="Aktive løp" value={analytics.active} detail="Pågående oppfølging" icon="⚡" tone="bg-emerald-50 text-emerald-600" />
        <MetricCard label="Ventende oppgaver" value={analytics.pending} detail="Krever neste handling" icon="⏳" tone="bg-amber-50 text-amber-600" />
        <MetricCard label="Gjennomføring" value={`${analytics.completionRate}%`} detail={`${analytics.completed} fullført`} icon="📈" tone="bg-violet-50 text-violet-600" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <h3 className="text-lg font-black text-slate-900">Ytelse per sekvens</h3>
        </div>

        {analytics.sequenceRows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500 font-medium">
            Ingen sekvenser tilgjengelig ennå.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {analytics.sequenceRows.map(sequence => (
              <div key={sequence.sequence_id} className="p-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-5 items-center">
                <div className="min-w-0">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="min-w-0">
                      <h4 className="font-black text-slate-900 truncate">{sequence.name}</h4>
                      <p className="text-xs text-slate-500 font-medium mt-1">
                        {sequence.enrolled} påmeldt · {sequence.active} aktive · {sequence.pending} ventende oppgaver
                      </p>
                    </div>
                    <span className="text-sm font-black text-slate-700">{sequence.completionRate}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transition-all"
                      style={{ width: `${sequence.completionRate}%` }}
                    />
                  </div>
                </div>
                <span className="px-3 py-1.5 rounded-lg bg-slate-50 text-xs font-black text-slate-500 uppercase tracking-widest">
                  {sequence.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-400 font-medium">
        <SafeIcon icon="ℹ️" />
        <span>Åpnings- og svarrater vises når e-posthendelser er tilgjengelige.</span>
      </div>
    </div>
  );
};

export default SequencePerformanceAnalytics;